import { ExpandAltOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Radio, Table, type TableProps, Tooltip } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { isBoolean, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { SexuallyInappropriatesQuery } from '../../../../__generated__/graphql';
import { DownVote, UpVote } from '../../../../assets/svg';
import { FINAL_RATING_OPTIONS, LIMIT } from '../../../../common/constants';
import {
  formatCurrency,
  notEmpty,
  truncateToChars,
} from '../../../../common/utils';
import ActionBtn from '../../../../components/ActionBtn';
import useRouter from '../../../../hooks/useRouter';
import { VoteEnum } from '../../../../types/common.type';
import { GET_SEXUALLY_INAPPROPRIATE_ANALYSES } from '../../graphql/queries';
import SexuallyInappropriateDetailsModal from './SexuallyInappropriateDetailsModal';

export type SexuallyInappropriateAnalysesData = NonNullable<
  NonNullable<
    SexuallyInappropriatesQuery['sexuallyInappropriates_connection']
  >['nodes']
>[number];

const SexuallyInappropriateAnalyses = ({
  externalFiltersSection,
  onTotalChange,
  activeTab,
  setIsDataPresent,
}: {
  externalFiltersSection?: {
    section?: {
      label: string;
      value: string;
      key: string;
    } | null;
    isProcessed?: boolean;
  };
  onTotalChange: ({ token, count }: { token: number; count: number }) => void;
  activeTab:
    | 'factCheck'
    | 'category'
    | 'sexually-inappropriate'
    | 'religious-transparency';
  setIsDataPresent: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    params: { bookId = '' },
  } = useRouter();

  const [sexuallyInappropriateData, setSexuallyInappropriateData] = useState<
    SexuallyInappropriateAnalysesData[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [totalData, setTotalData] = useState<number>(0);

  const [variables, setVariables] = useState({
    filters: {
      book: {
        documentId: {
          eq: bookId ?? '',
        },
      },
      subSection: {},
    },
    pagination: {
      page: 1,
      pageSize: LIMIT,
    },
    sort: ['createdAt:asc'],
  });

  const {
    data: sexuallyAppropriateDataList,
    loading,
    refetch,
  } = useQuery(GET_SEXUALLY_INAPPROPRIATE_ANALYSES, {
    skip:
      isEmpty(bookId) || !activeTab || activeTab !== 'sexually-inappropriate',
    variables: {
      ...variables,
      filters: {
        ...variables.filters,
        subSection: {
          ...variables?.filters?.subSection,
          ...(isBoolean(externalFiltersSection?.isProcessed)
            ? {
                isProcessed: {
                  eq: externalFiltersSection?.isProcessed,
                },
              }
            : { isProcessed: undefined }),
        },
        ...(externalFiltersSection?.section
          ? {
              section: {
                title: {
                  eqi: externalFiltersSection.section.value,
                },
              },
            }
          : { section: undefined }),
      },
    },
    onCompleted({ sexuallyInappropriates_connection }) {
      setTotalData(sexuallyInappropriates_connection?.pageInfo.total ?? 0);
      const sectionIsSelected = externalFiltersSection?.section;
      const sexuallyInappropriateData =
        sexuallyInappropriates_connection?.nodes?.[0];
      onTotalChange({
        token: sectionIsSelected
          ? sexuallyInappropriateData?.section?.tokenCounts ?? 0
          : sexuallyInappropriateData?.book?.tokenCounts ?? 0,
        count: sectionIsSelected
          ? sexuallyInappropriateData?.section?.priceCounts ?? 0
          : sexuallyInappropriateData?.book?.priceCounts ?? 0,
      });

      const sexuallyInappropriate =
        sexuallyInappropriates_connection?.nodes?.filter(notEmpty);
      const newData = sexuallyInappropriate?.map((sexuallyInappropriate) => ({
        ...sexuallyInappropriate,
        key: sexuallyInappropriate?.documentId,
      }));
      setSexuallyInappropriateData(newData ?? []);
      setIsDataPresent(!!newData?.length);
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError() {
      onTotalChange({
        token: 0,
        count: 0,
      });
    },
  });

  const getSortOrder = (column: 'finalRating') => {
    const sorting = variables?.sort?.[0];
    const [sortOn, sortBy] = sorting?.split(':') ?? [];
    return sortOn === column ? (sortBy === 'asc' ? 'ascend' : 'descend') : null;
  };

  const columns: TableProps<SexuallyInappropriateAnalysesData>['columns'] = [
    {
      title: 'Input (Paragraph)',
      dataIndex: ['subSection', 'paragraph'],
      key: 'paragraph',
      width: 300,
      render: (value) => truncateToChars(value, 100),
    },
    {
      title: 'Original Final Rating',
      dataIndex: 'finalRating',
      key: 'finalRating',
      width: 150,
      sorter: true,
      defaultSortOrder: 'ascend',
      sortOrder: getSortOrder('finalRating'),
      render: (finalRating) => {
        return <p>{finalRating ?? '-'}</p>;
      },
      filterMultiple: false,
      filters: FINAL_RATING_OPTIONS.map(({ label, value }) => ({
        text: label,
        value,
      })),
    },
    {
      title: 'Nudity Detected',
      dataIndex: 'nudityDetected',
      key: 'nudityDetected',
      width: 100,
      render: (nudityDetected) => {
        return <p>{nudityDetected?.join(', ') ?? '-'}</p>;
      },
    },
    {
      title: 'Sexual Acts Detected',
      dataIndex: 'sexualActsDetected',
      key: 'sexualActsDetected',
      width: 100,
      render: (sexualActsDetected) => {
        return <p>{sexualActsDetected?.join(', ') ?? '-'}</p>;
      },
    },
    {
      title: 'Rating Reason',
      dataIndex: 'ratingReason',
      key: 'ratingReason',
      width: 200,
      render: (value) => truncateToChars(value, 100),
    },
    {
      title: 'Feedback',
      dataIndex: ['subSection', 'feedback'],
      key: 'feedback',
      width: 100,
      render: (value) => {
        return value ? (
          value.sexuallyUpvote ? (
            <Tooltip title={value?.sexuallyClientComments ?? ''}>
              <Radio.Group className="vote">
                <Radio.Button value={true} className="up-vote">
                  <UpVote />
                </Radio.Button>
              </Radio.Group>
            </Tooltip>
          ) : value.sexuallyDownvote ? (
            <Tooltip title={value?.sexuallyClientComments ?? ''}>
              <Radio.Group className="vote">
                <Radio.Button value={false} className="down-vote">
                  <DownVote />
                </Radio.Button>
              </Radio.Group>
            </Tooltip>
          ) : (
            '-'
          )
        ) : (
          '-'
        );
      },
      filterMultiple: false,
      filters: [
        {
          text: VoteEnum.UpVote,
          value: VoteEnum.UpVote,
        },
        {
          text: VoteEnum.DownVote,
          value: VoteEnum.DownVote,
        },
        {
          text: VoteEnum.NoVote,
          value: VoteEnum.NoVote,
        },
      ],
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 90,
      render: (value) => formatCurrency({ value: value ?? 0 }),
    },
    {
      title: 'Action',
      dataIndex: 'documentId',
      key: 'documentId',
      fixed: 'right',
      width: 80,
      align: 'center',
      render: (_, __, index) => {
        return (
          <ActionBtn
            icon={<ExpandAltOutlined />}
            type="text"
            onClick={() => {
              setActiveIndex(index);
              setIsModalOpen(true);
            }}
          />
        );
      },
    },
  ];

  useEffect(() => {
    if (loading || activeTab === 'religious-transparency') return;
    refetch();
  }, [activeTab]);

  const changePage = (page: number) => {
    setVariables((prev) => ({
      ...prev,
      pagination: {
        ...prev?.pagination,
        page,
      },
    }));
  };

  const modifySexuallyInappropriateData = (
    id: string,
    value: {
      sexuallyFirstPersonPerspective?: boolean | null;
      sexuallyPhysicalContactType?: string[] | null;
      sexuallySetting?: string[] | null;
      sexuallyExplicitTermsFound?: boolean | null;
      sexuallyExplicitTermsList?: string[] | null;
      sexuallyAnatomicalTermsPresent?: boolean | null;
      sexuallyAnatomicalTermsList?: string[] | null;
      sexuallyContainsDialogue?: boolean | null;
      sexuallyChromosomallyDefinedGender?: string[] | null;
      sexuallySelfIdentifiedGenders?: string[] | null;
      sexualRelationshipType?: string[];
      characterAge?: string[];
      euphemismsList?: string[];
      suggestivePhrasesList?: string[];
      powerImbalancePresent?: boolean | null;
      tone?: string[];
      excessiveFocusOnBodyDescription?: boolean | null;
      languageOfSecrecyOrConcealment?: boolean | null;
      roleplayOrFantasyContext?: boolean | null;
      voilenceOverlap?: boolean | null;
      nudityDetected?: string[];
      nudityExtent?: string[];
      nudityType?: string[];
      sexualActsDetected?: string[];
      sexuallyClientComments?: string;
      sexuallyUpvote?: boolean;
      sexuallyDownvote?: boolean;
      sexuallySexualRelationshipType?: string[];
      sexuallyCharacterAge?: string[];
      sexuallyEuphemismsList?: string[];
      sexuallySuggestivePhrasesList?: string[];
      sexuallyPowerImbalancePresent?: boolean | null;
      sexuallyTone?: string[];
      sexuallyExcessiveFocusOnBodyDescription?: boolean | null;
      sexuallyLanguageOfSecrecyOrConcealment?: boolean | null;
      sexuallyRoleplayOrFantasyContext?: boolean | null;
      sexuallyViolenceOverlap?: boolean | null;
      sexuallyNudityDetected?: string[];
      sexuallyNudityExtent?: string[];
      sexuallyNudityType?: string[];
      sexuallySexualActsDetected?: string[];
      sexuallyFinalRating?: number | null;
      sexuallyRatingReason?: string | null;
    },
  ) => {
    const modifyData =
      sexuallyInappropriateData.map((item) =>
        item.documentId === id
          ? {
              ...item,
              subSection: {
                ...item.subSection,
                documentId: item.subSection?.documentId ?? '',
                feedback: {
                  ...item.subSection?.feedback,
                  ...value,
                },
              },
            }
          : item,
      ) ?? [];

    setSexuallyInappropriateData(modifyData);
  };

  const selectSexuallyInappropriateData:
    | SexuallyInappropriateAnalysesData
    | undefined = useMemo(
    () => sexuallyInappropriateData[activeIndex],
    [activeIndex, sexuallyInappropriateData],
  );

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleTableChange: TableProps<SexuallyInappropriateAnalysesData>['onChange'] =
    (pagination, filters, sorter) => {
      const { current = 1, pageSize = LIMIT } = pagination ?? {};
      const feedback = filters?.feedback?.[0]
        ? filters.feedback?.[0] === VoteEnum.UpVote
          ? { sexuallyUpvote: { eq: true } }
          : filters.feedback?.[0] === VoteEnum.DownVote
            ? { sexuallyUpvote: { eq: false }, sexuallyDownvote: { eq: true } }
            : filters.feedback?.[0] === VoteEnum.NoVote
              ? { sexuallyUpvote: { eq: null } }
              : undefined
        : undefined;

      const finalRatingFilter = filters?.finalRating?.[0]
        ? { eq: Number(filters.finalRating[0]) }
        : undefined;
      const { field, order } =
        sorter as SorterResult<SexuallyInappropriateAnalysesData>;
      const sort = order
        ? [`${field}:${order === 'ascend' ? 'asc' : 'desc'}`]
        : [];
      setVariables((prev) => ({
        ...prev,
        filters: {
          ...prev?.filters,
          finalRating: finalRatingFilter,
          subSection: { ...prev.filters?.subSection, feedback },
        },
        pagination: {
          ...prev.pagination,
          page: current,
          pageSize,
        },
        sort,
      }));
    };

  return (
    <div>
      <Table<SexuallyInappropriateAnalysesData>
        columns={columns}
        dataSource={sexuallyInappropriateData}
        scroll={{
          x: 'max-content',
          y: 'calc(100vh - 255px)',
        }}
        pagination={{
          current: variables?.pagination?.page ?? 0,
          pageSize: variables?.pagination?.pageSize ?? LIMIT,
          total:
            sexuallyAppropriateDataList?.sexuallyInappropriates_connection
              ?.pageInfo?.total ?? 0,
        }}
        loading={loading}
        onChange={handleTableChange}
      />
      <SexuallyInappropriateDetailsModal
        modalOpen={isModalOpen}
        closeModal={closeModal}
        selectSexuallyInappropriateData={selectSexuallyInappropriateData}
        setActiveIndex={setActiveIndex}
        activeIndex={activeIndex}
        pagination={{
          ...variables?.pagination,
          total: totalData,
          listDataCount: sexuallyInappropriateData?.length ?? 0,
        }}
        changePage={changePage}
        loading={loading}
        modifySexuallyInappropriateData={modifySexuallyInappropriateData}
      />
    </div>
  );
};

export default SexuallyInappropriateAnalyses;
